import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "../client";

export const PORTFOLIO_TRACKER_QUERY_KEY = ["portfolio-tracker"] as const;

/** Mirrors the API's PortfolioTrackerSplit. Sent as a number. */
export const PortfolioTrackerSplit = {
  Unknown: 0,
  Percent: 1,
  Amount: 2,
} as const;

export type PortfolioTrackerSplitValue = (typeof PortfolioTrackerSplit)[keyof typeof PortfolioTrackerSplit];

/** A strategy the customer receives signals from. */
export interface TrackerStrategyDto {
  tradingStrategyId: string;
  publicStrategyId: string;
  name: string;
  chartedSymbol: string | null;
  bullSymbol: string | null;
  bearSymbol: string | null;
  /** Scr.Enums.StrategyDirection. */
  direction: number;
  /** Scr.Enums.Timeframe. */
  timeframe: number;
  following: boolean;
}

export interface TrackerSetupStrategyDto {
  tradingStrategyId: string;
  /** By percent only. */
  allocationPercent: number | null;
  /** By amount only. */
  amount: number | null;
  /** Only when each strategy has its own start. */
  start: string | null;
}

export interface TrackerSetupDto {
  split: PortfolioTrackerSplitValue;
  /** The total, by percent only. */
  amount: number | null;
  startsPerStrategy: boolean;
  /** The one start for every strategy. Null when each has its own. */
  start: string | null;
  savedAt: string;
  /** In the customer's order, which sets each strategy's colour. */
  strategies: TrackerSetupStrategyDto[];
}

export type TrackerSetupRequest = Omit<TrackerSetupDto, "savedAt">;

export interface TrackerTradeDto {
  symbol: string;
  buyDate: string;
  exitDate: string;
  /** Null when neither the fill nor the alert carries a price. */
  buyPrice: number | null;
  exitPrice: number | null;
  /** No fill price, so the alert's price stands in. */
  buyPricedAtAlert: boolean;
  exitPricedAtAlert: boolean;
  /** Null when a leg has no price, which leaves the trade out of every figure. */
  returnPercent: number | null;
  /** The balance its allocation was taken from. */
  balanceAtBuy: number | null;
  /** The money the buy used. */
  amount: number | null;
  /** Cut back because the free cash ran short that day. */
  reduced: boolean;
  profit: number | null;
}

export interface TrackerOpenTradeDto {
  symbol: string;
  buyDate: string;
  buyPrice: number | null;
  buyPricedAtAlert: boolean;
  balanceAtBuy: number | null;
  amount: number | null;
}

export interface TrackerStrategyResultDto {
  tradingStrategyId: string;
  /** By amount, its amount as a share of what was put in. */
  allocationPercent: number;
  start: string;
  profit: number;
  won: number;
  lost: number;
  /** Null when it isn't up. */
  shareOfGainsPercent: number | null;
  /** Its own profit, from a high to a later low. */
  biggestDrop: { amount: number; from: string; to: string } | null;
  /** Closed trades from its start, priced or not, newest exit first. */
  trades: TrackerTradeDto[];
  openTrade: TrackerOpenTradeDto | null;
  /** The latest trade bought before its start that ended on or after it, which isn't counted. */
  exitBeforeStart: { buyDate: string; exitDate: string | null; symbol: string } | null;
}

export interface TrackerDrawdownDto {
  /** Positive: 4.3 is 4.3% below the high. */
  percent: number;
  from: string | null;
  to: string | null;
  backOn: string | null;
  longestWaitDays: number;
}

export interface TrackerResultsDto {
  putIn: number;
  worth: number;
  profit: number;
  firstStart: string;
  cashPercent: number;
  closedTrades: number;
  won: number;
  lost: number;
  openTrades: number;
  unpricedTrades: number;
  alertPricedTrades: number;
  drawdown: TrackerDrawdownDto;
  /** From the first start at what was put in, a point per exit, to today. */
  balance: { date: string; value: number }[];
  /** At or below zero, a point per day, to today. */
  fallFromHigh: { date: string; percent: number }[];
  /** In the setup's order. */
  strategies: TrackerStrategyResultDto[];
  /** In the setup, but the customer no longer receives their signals. */
  leftOut: { tradingStrategyId: string; name: string }[];
}

export interface PortfolioTrackerDto {
  /** The server's today. */
  asOf: string;
  /** The first signal of any strategy the customer receives. */
  signalsFrom: string | null;
  strategies: TrackerStrategyDto[];
  setup: TrackerSetupDto | null;
  results: TrackerResultsDto | null;
}

/** The customer's strategies, their saved setup and its results, worked out afresh on each read. */
export function usePortfolioTracker() {
  return useQuery<PortfolioTrackerDto>({
    queryKey: PORTFOLIO_TRACKER_QUERY_KEY,
    queryFn: async () => {
      const res = await apiClient.get<PortfolioTrackerDto>("/customers/me/portfolio-tracker");
      return res.data;
    },
  });
}

/** Saves a new version of the setup, then reads the results for it. */
export function useSavePortfolioTracker() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (setup: TrackerSetupRequest) => {
      await apiClient.put("/customers/me/portfolio-tracker", setup);
    },
    // Returned so the save isn't finished until the results for it have arrived.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PORTFOLIO_TRACKER_QUERY_KEY }),
  });
}
